// === GESTIONNAIRE DE BUG REPORTS ===
// Envoie les rapports à un webhook Discord (URL via VITE_DISCORD_WEBHOOK_URL)
// + stocke en localStorage comme fallback offline et historique consultable.

const REPORTS_KEY = 'deadtide_bug_reports';
const MAX_REPORTS = 50;
const LAST_SENT_KEY = 'deadtide_bug_last_sent';
const ANTI_SPAM_MS = 30_000; // 30s mini entre 2 envois (anti-spam client)

export interface BugReport {
  id: string;
  timestamp: string;
  pseudo: string;
  description: string;
  version: string;
  userAgent: string;
  resolution: string;
  scene: string;
  /** Contexte de run optionnel (étage, or, équipe, etc.) */
  context?: string;
  /** Statut d'envoi Discord — utile pour debug et retry manuel */
  sent?: boolean;
  sendError?: string;
}

export interface SubmitResult {
  ok: boolean;
  /** True si l'envoi Discord a réussi, false si seul le stockage local a marché */
  sentToDiscord: boolean;
  message: string;
}

export class BugReportManager {
  /** URL du webhook Discord (depuis env). Si absente → stockage local seulement. */
  private static getWebhookUrl(): string | undefined {
    return import.meta.env.VITE_DISCORD_WEBHOOK_URL as string | undefined;
  }

  /** Retourne tous les rapports (du plus récent au plus ancien) */
  static getAll(): BugReport[] {
    try {
      const raw = localStorage.getItem(REPORTS_KEY);
      if (!raw) return [];
      return JSON.parse(raw) as BugReport[];
    } catch {
      return [];
    }
  }

  /**
   * Soumet un nouveau rapport. Stocke localement en premier (toujours), puis
   * tente l'envoi Discord (best-effort). Retourne le résultat à l'UI pour
   * feedback approprié au testeur.
   */
  static async submit(data: Omit<BugReport, 'id' | 'timestamp' | 'sent' | 'sendError'>): Promise<SubmitResult> {
    // Anti-spam : refuser si dernier envoi < 30s
    try {
      const last = parseInt(localStorage.getItem(LAST_SENT_KEY) ?? '0', 10);
      if (Date.now() - last < ANTI_SPAM_MS) {
        const remain = Math.ceil((ANTI_SPAM_MS - (Date.now() - last)) / 1000);
        return { ok: false, sentToDiscord: false, message: `⏳ Patiente ${remain}s avant le prochain rapport.` };
      }
    } catch { /* localStorage indispo */ }

    const report: BugReport = {
      ...data,
      id: `bug_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      timestamp: new Date().toISOString(),
      sent: false,
    };

    // 1. Stockage local TOUJOURS en premier (fallback offline + historique)
    try {
      const reports = BugReportManager.getAll();
      reports.unshift(report);
      if (reports.length > MAX_REPORTS) reports.splice(MAX_REPORTS);
      localStorage.setItem(REPORTS_KEY, JSON.stringify(reports));
    } catch {
      // localStorage indisponible — on continue quand même pour tenter Discord
    }

    // 2. Envoi Discord (best-effort, async)
    const url = BugReportManager.getWebhookUrl();
    if (!url) {
      return {
        ok: true,
        sentToDiscord: false,
        message: '✅ Rapport enregistré localement (webhook non configuré).',
      };
    }

    try {
      const payload = BugReportManager.buildDiscordPayload(report);
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        // Discord répond souvent 204 No Content sur succès, ou 429 si rate-limit
        const errText = `Discord ${resp.status}: ${await resp.text().catch(() => '')}`.slice(0, 200);
        BugReportManager.markSendError(report.id, errText);
        return { ok: true, sentToDiscord: false, message: `✅ Enregistré localement. ⚠️ ${errText}` };
      }
      // Succès : marquer envoyé + sauvegarder le timestamp anti-spam
      BugReportManager.markSent(report.id);
      try { localStorage.setItem(LAST_SENT_KEY, String(Date.now())); } catch { /* */ }
      return { ok: true, sentToDiscord: true, message: '✅ Rapport envoyé ! Merci pour ton aide 🏴‍☠️' };
    } catch (e) {
      const errText = (e instanceof Error ? e.message : String(e)).slice(0, 200);
      BugReportManager.markSendError(report.id, errText);
      return { ok: true, sentToDiscord: false, message: `✅ Enregistré localement. ⚠️ Réseau : ${errText}` };
    }
  }

  /** Construit le payload Discord (embed structuré) */
  private static buildDiscordPayload(r: BugReport): object {
    const fields = [
      { name: '📝 Description', value: r.description.slice(0, 1024), inline: false },
      { name: '🎮 Version', value: `v${r.version}`, inline: true },
      { name: '🎬 Scène', value: r.scene, inline: true },
      { name: '🖥️ Résolution', value: r.resolution, inline: true },
    ];
    if (r.context) {
      fields.push({ name: '🗺️ Contexte run', value: r.context.slice(0, 1024), inline: false });
    }
    fields.push({ name: '🌐 User Agent', value: r.userAgent.slice(0, 200), inline: false });

    return {
      username: 'Deadtide Bug Report',
      embeds: [{
        title: `🐛 Bug — ${r.pseudo}`,
        color: 0xe74c3c, // rouge
        fields,
        timestamp: r.timestamp,
        footer: { text: r.id },
      }],
    };
  }

  /** Marque un rapport comme envoyé avec succès */
  private static markSent(id: string): void {
    try {
      const reports = BugReportManager.getAll();
      const r = reports.find(x => x.id === id);
      if (r) {
        r.sent = true;
        r.sendError = undefined;
        localStorage.setItem(REPORTS_KEY, JSON.stringify(reports));
      }
    } catch { /* */ }
  }

  /** Marque un rapport avec une erreur d'envoi (pour retry manuel éventuel) */
  private static markSendError(id: string, error: string): void {
    try {
      const reports = BugReportManager.getAll();
      const r = reports.find(x => x.id === id);
      if (r) {
        r.sent = false;
        r.sendError = error;
        localStorage.setItem(REPORTS_KEY, JSON.stringify(reports));
      }
    } catch { /* */ }
  }

  /** Efface tous les rapports */
  static clearAll(): void {
    try {
      localStorage.removeItem(REPORTS_KEY);
    } catch {
      // localStorage indisponible
    }
  }
}
